/* eslint-disable no-await-in-loop */
import defaultHeaders from './default-headers';

/**
 * Fetch POST request utility
 * @param {string} url - The URL to send the POST request to.
 * @param {any} data - The data to be sent in the request body.
 * @param {boolean} isFileUpload - Whether the request is for a file upload.
 * @param {(progress: any) => void} [onProgress] - Optional callback for progress updates.
 * @returns {Promise<Response>} - The response from the server.
 */
const postStream = async (
  url: string,
  data: any,
  isFileUpload = false,
  onProgress?: (progress: any) => void,
): Promise<Response> => {
  try {
    const customHeaders: HeadersInit = isFileUpload
      ? {} // FormData automatically sets the headers
      : { 'Content-Type': 'application/json' };

    const authToken = defaultHeaders();
    const headers: HeadersInit = {
      ...(authToken && authToken),
      ...customHeaders,
    };

    const requestBody = isFileUpload ? data : JSON.stringify(data);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: requestBody,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Handle response stream if onProgress callback is provided
    if (onProgress) {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let done = false;

      while (!done && reader) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        const chunk = decoder.decode(value);
        const updates = chunk.split('\n').filter(Boolean); // Process each new-line delimited message

        updates.forEach((update) => {
          try {
            const parsedUpdate = JSON.parse(update);

            // Check for backend-sent error and handle it
            if (parsedUpdate.error) {
              // Construct custom error object
              const customError = new Error(parsedUpdate.message);
              (customError as any).response = {
                data: {
                  message: parsedUpdate.message,
                },
              };
              throw customError;
            }

            onProgress(parsedUpdate); // Call the progress update callback
          } catch (error) {
            console.error('Error parsing JSON update:', error);
            throw error; // Re-throw the error to exit the loop
          }
        });
      }
    }

    return response;
  } catch (error) {
    console.error('Error in postRequest:', error);
    throw error;
  }
};

export default postStream;
